import { AfterViewInit, Component, ErrorHandler, HostListener, OnInit, ViewChild, ChangeDetectionStrategy, computed, signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { email, form, required, validate } from '@angular/forms/signals'
import { Router } from '@angular/router'
import { arvonMuutokset } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'

import { MatDialog } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'

import { KayttajaService } from '../_angular/service/kayttaja.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'

import { AloitaSalasananPalautusProsessiPyynto, AloitaSalasananPalautusProsessiVastaus } from '@lemon/core-firebase/model/common'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { VaihdaKieliDialog, VaihdaKieliDialogData } from '../vaihda-kieli.dialog'

interface UnohdettuSalasanaLomake {
  email: string
}

@Component({
  templateUrl: './forgot.component.html',
  styleUrls: ['./login.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ForgotComponent implements OnInit, AfterViewInit {
  @ViewChild(MatInput) emailInput: MatInput

  private readonly _palvelimenVirhe = signal<string | null>(null)

  lomake = form(signal<UnohdettuSalasanaLomake>({ email: '' }), polku => {
    required(polku.email)
    email(polku.email)
    validate(polku.email, () => {
      const laji = this._palvelimenVirhe()
      return laji ? { kind: laji } : null
    })
  })
  private readonly _kieli = toSignal(this.translationService.currentLanguageObservable)
  readonly fieldErrors = computed(() => {
    // Virheviestit käännetään nykyisellä kielellä, joten kielen vaihto muodostaa ne uudelleen
    this._kieli()
    return this.validationService.updateValidationStatus('login', this.lomake)
  })
  readonly commonError = signal<string | null>(null)
  readonly success = signal(false)
  online = true
  year = new Date().getFullYear()

  constructor(
    private kayttajaService: KayttajaService,
    private dialog: MatDialog,
    private router: Router,
    private validationService: FormValidationService,
    private errorHandler: ErrorHandler,
    private ladataanService: LadataanService,
    private translationService: LemonTranslationService,
    private _firebaseLemonaid: FirebaseLemonaid,
  ) {
    this.kayttajaService.getKayttajanTiedot().then(kayttajanTiedot => {
      if (kayttajanTiedot) {
        this.router.navigate(['/'])
      }
    })
    // Palvelimen virhe on voimassa, kunnes kentän arvo muuttuu, kuten reaktiivisen lomakkeen setErrors
    arvonMuutokset(this.lomake.email).subscribe(() => this._palvelimenVirhe.set(null))
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.emailInput.focus()
    }, 0)
  }

  setLoginProcessStarted() {
    this.ladataanService.aloitaLataaminen()
  }

  setLoginProcessEnded() {
    this.ladataanService.lopetaLataaminen()
  }

  @HostListener('document:keydown.esc')
  back() {
    this.router.navigate(['/kirjaudu'])
  }

  @HostListener('document:keydown.enter')
  login() {
    if (!this.lomake().valid()) {
      this.validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this.setLoginProcessStarted()
    this.commonError.set(null)

    const req: AloitaSalasananPalautusProsessiPyynto = {
      email: this.lomake.email().value(),
    }
    this._firebaseLemonaid
      .functionsCall<AloitaSalasananPalautusProsessiPyynto, AloitaSalasananPalautusProsessiVastaus>('passwordResetSendEmail', req)
      .then(result => {
        const code = result.e
        if (code) {
          switch (code) {
            case 'auth/user-not-found':
              this._palvelimenVirhe.set('user-not-found')
              break
            case 'auth/invalid-email':
              this._palvelimenVirhe.set('invalid-email')
              break
            case 'auth/user-disabled':
              this._palvelimenVirhe.set('user-disabled')
              break
            default:
              this.commonError.set(this.translationService.lokalisoi('yleiset.tuntematon-virhe'))
          }
          this.validationService.merkitseKokoLomakeKosketuksi(this.lomake)
          this.setLoginProcessEnded()
        } else {
          this.success.set(true)
          this.setLoginProcessEnded()
        }
      })
      .catch(err => {
        this.commonError.set(this.translationService.lokalisoi('yleiset.tuntematon-virhe'))
        this.errorHandler.handleError(err)
        this.setLoginProcessEnded()
      })
  }

  vaihdaKieli() {
    const data: VaihdaKieliDialogData = {
      tallenna: false,
    }
    this.dialog.open(VaihdaKieliDialog, { data: data })
  }
}
